<template>
    <div class="min-h-screen bg-gray-50/50">
        <div class="mx-auto max-w-7xl space-y-8 px-4 py-8 sm:px-6 lg:px-8">
            <div>
                <h1 class="text-xl font-semibold text-gray-800">
                    {{ t("vue.kiosk_ai_feedback") }}
                </h1>
                <p class="text-sm text-gray-500">
                    {{ t("vue.kiosk_ai_feedback_description") }}
                </p>
            </div>

            <section class="space-y-4">
                <div>
                    <h2 class="text-base font-semibold text-gray-800">
                        {{ t("vue.kiosk_ai_feedback_dashboard_title") }}
                    </h2>
                    <p class="text-xs text-gray-500">
                        {{ t("vue.kiosk_ai_feedback_dashboard_description") }}
                    </p>
                </div>

                <div
                    class="flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 bg-white px-5 py-3"
                >
                    <span
                        class="text-xs font-medium uppercase tracking-wide text-gray-400"
                    >
                        {{ t("vue.kiosk_ai_feedback_dashboard_filter") }}
                    </span>

                    <select
                        v-model="summaryType"
                        class="rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 focus:border-gray-400 focus:outline-hidden"
                    >
                        <option value="">
                            {{ t("vue.kiosk_ai_feedback_all_types") }}
                        </option>
                        <option
                            v-for="option in feedback.available_types"
                            :key="option"
                            :value="option"
                        >
                            {{ typeLabel(option) }}
                        </option>
                    </select>

                    <input
                        v-model="summaryTeamId"
                        type="number"
                        min="1"
                        :placeholder="t('vue.kiosk_ai_feedback_team_id')"
                        class="w-40 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 placeholder:text-gray-400 focus:border-gray-400 focus:outline-hidden"
                    />

                    <input
                        v-model="summaryChatId"
                        type="number"
                        min="1"
                        :placeholder="t('vue.kiosk_ai_feedback_chat_id')"
                        class="w-40 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 placeholder:text-gray-400 focus:border-gray-400 focus:outline-hidden"
                    />

                    <button
                        v-if="hasSummaryFilters"
                        type="button"
                        class="cursor-pointer rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-600 transition hover:bg-gray-50"
                        @click="clearSummaryFilters"
                    >
                        {{ t("vue.kiosk_ai_feedback_clear_filters") }}
                    </button>
                </div>

                <div
                    v-if="summary.has_data === false"
                    class="rounded-xl border border-dashed border-gray-200 bg-white px-5 py-8 text-center"
                >
                    <p class="text-sm font-medium text-gray-600">
                        {{ t("vue.kiosk_ai_feedback_summary_empty") }}
                    </p>
                    <p class="mt-1 text-xs text-gray-400">
                        {{ t("vue.kiosk_ai_feedback_summary_empty_hint") }}
                    </p>
                </div>

                <template v-else>
                    <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
                        <div
                            class="rounded-xl border border-gray-200 bg-white p-5"
                        >
                            <p
                                class="text-xs font-medium uppercase tracking-wide text-gray-400"
                            >
                                {{ t("vue.kiosk_ai_feedback_summary_rated") }}
                            </p>
                            <p
                                class="mt-1 text-2xl font-semibold text-gray-800"
                            >
                                {{ formatCount(summary.overall.total_count) }}
                            </p>
                            <p class="mt-1 text-xs text-gray-400">
                                {{ t("vue.kiosk_ai_feedback_summary_scope") }}
                            </p>
                        </div>

                        <div
                            class="rounded-xl border border-gray-200 bg-white p-5"
                        >
                            <p
                                class="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gray-400"
                            >
                                <HandThumbUpIcon
                                    class="h-3.5 w-3.5 text-emerald-600"
                                />
                                {{ t("vue.kiosk_ai_feedback_rating_up") }}
                            </p>
                            <p
                                class="mt-1 text-2xl font-semibold text-gray-800"
                            >
                                {{ formatCount(summary.overall.up_count) }}
                            </p>
                            <p class="mt-1 text-xs text-gray-400">
                                {{ formatShare(summary.overall.up_share) }}
                            </p>
                        </div>

                        <div
                            class="rounded-xl border border-gray-200 bg-white p-5"
                        >
                            <p
                                class="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gray-400"
                            >
                                <HandThumbDownIcon
                                    class="h-3.5 w-3.5 text-red-500"
                                />
                                {{ t("vue.kiosk_ai_feedback_rating_down") }}
                            </p>
                            <p
                                class="mt-1 text-2xl font-semibold text-gray-800"
                            >
                                {{ formatCount(summary.overall.down_count) }}
                            </p>
                            <p class="mt-1 text-xs text-gray-400">
                                {{ formatShare(summary.overall.down_share) }}
                            </p>
                        </div>
                    </div>

                    <div class="rounded-xl border border-gray-200 bg-white">
                        <div
                            class="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-5 py-3"
                        >
                            <h3 class="text-sm font-semibold text-gray-700">
                                {{
                                    t("vue.kiosk_ai_feedback_summary_polarity")
                                }}
                            </h3>
                            <div
                                class="flex items-center gap-4 text-xs text-gray-500"
                            >
                                <span class="inline-flex items-center gap-1.5">
                                    <HandThumbUpIcon
                                        class="h-3.5 w-3.5 text-emerald-600"
                                    />
                                    {{ t("vue.kiosk_ai_feedback_rating_up") }}
                                </span>
                                <span class="inline-flex items-center gap-1.5">
                                    <HandThumbDownIcon
                                        class="h-3.5 w-3.5 text-red-500"
                                    />
                                    {{ t("vue.kiosk_ai_feedback_rating_down") }}
                                </span>
                            </div>
                        </div>

                        <div class="space-y-4 px-5 py-4">
                            <div
                                v-for="row in polarityRows"
                                :key="row.key"
                                class="space-y-1.5"
                            >
                                <div
                                    class="flex flex-wrap items-center justify-between gap-2 text-xs"
                                >
                                    <span class="font-medium text-gray-600">
                                        {{ t(row.label) }}
                                    </span>
                                    <span
                                        v-if="row.polarity.total_count === 0"
                                        class="text-gray-400"
                                    >
                                        {{
                                            t(
                                                "vue.kiosk_ai_feedback_summary_no_ratings",
                                            )
                                        }}
                                    </span>
                                    <span v-else class="text-gray-400">
                                        {{ formatShare(row.polarity.up_share) }}
                                        ·
                                        {{ formatCount(row.polarity.up_count) }}
                                        /
                                        {{
                                            formatCount(row.polarity.down_count)
                                        }}
                                    </span>
                                </div>

                                <div
                                    v-if="row.polarity.total_count === 0"
                                    class="h-2.5 rounded-full bg-gray-100"
                                />
                                <div v-else class="flex h-2.5 gap-0.5">
                                    <div
                                        v-if="row.polarity.up_count > 0"
                                        class="rounded-full bg-emerald-600"
                                        :style="{
                                            flex: `${row.polarity.up_count} 1 0%`,
                                        }"
                                        :title="
                                            segmentTitle('up', row.polarity)
                                        "
                                    />
                                    <div
                                        v-if="row.polarity.down_count > 0"
                                        class="rounded-full bg-red-500"
                                        :style="{
                                            flex: `${row.polarity.down_count} 1 0%`,
                                        }"
                                        :title="
                                            segmentTitle('down', row.polarity)
                                        "
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
            </section>

            <section class="space-y-4 border-t border-gray-200 pt-8">
                <div class="flex flex-wrap items-end justify-between gap-3">
                    <div>
                        <h2 class="text-base font-semibold text-gray-800">
                            {{ t("vue.kiosk_ai_feedback_table_title") }}
                        </h2>
                        <p class="text-xs text-gray-500">
                            {{ t("vue.kiosk_ai_feedback_table_description") }}
                        </p>
                    </div>
                    <p class="text-xs text-gray-400">
                        {{
                            t("vue.kiosk_ai_feedback_total", {
                                total: feedback.total_count,
                            })
                        }}
                    </p>
                </div>

                <div
                    class="flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 bg-white px-5 py-3"
                >
                    <span
                        class="text-xs font-medium uppercase tracking-wide text-gray-400"
                    >
                        {{ t("vue.kiosk_ai_feedback_table_filter") }}
                    </span>

                    <input
                        v-model="search"
                        type="search"
                        :placeholder="t('vue.kiosk_ai_feedback_search')"
                        class="w-56 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 placeholder:text-gray-400 focus:border-gray-400 focus:outline-hidden"
                    />

                    <select
                        v-model="type"
                        class="rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 focus:border-gray-400 focus:outline-hidden"
                    >
                        <option value="">
                            {{ t("vue.kiosk_ai_feedback_all_types") }}
                        </option>
                        <option
                            v-for="option in feedback.available_types"
                            :key="option"
                            :value="option"
                        >
                            {{ typeLabel(option) }}
                        </option>
                    </select>

                    <select
                        v-model="rating"
                        class="rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 focus:border-gray-400 focus:outline-hidden"
                    >
                        <option value="">
                            {{ t("vue.kiosk_ai_feedback_all_ratings") }}
                        </option>
                        <option value="up">
                            {{ t("vue.kiosk_ai_feedback_rating_up") }}
                        </option>
                        <option value="down">
                            {{ t("vue.kiosk_ai_feedback_rating_down") }}
                        </option>
                    </select>

                    <select
                        v-model="target"
                        class="rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 focus:border-gray-400 focus:outline-hidden"
                    >
                        <option value="">
                            {{ t("vue.kiosk_ai_feedback_all_targets") }}
                        </option>
                        <option value="chat">
                            {{ t("vue.kiosk_ai_feedback_target_chat") }}
                        </option>
                        <option value="message">
                            {{ t("vue.kiosk_ai_feedback_target_message_only") }}
                        </option>
                    </select>

                    <input
                        v-model="teamId"
                        type="number"
                        min="1"
                        :placeholder="t('vue.kiosk_ai_feedback_team_id')"
                        class="w-40 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 placeholder:text-gray-400 focus:border-gray-400 focus:outline-hidden"
                    />

                    <input
                        v-model="chatId"
                        type="number"
                        min="1"
                        :placeholder="t('vue.kiosk_ai_feedback_chat_id')"
                        class="w-40 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 placeholder:text-gray-400 focus:border-gray-400 focus:outline-hidden"
                    />

                    <button
                        v-if="hasFilters"
                        type="button"
                        class="cursor-pointer rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-600 transition hover:bg-gray-50"
                        @click="clearFilters"
                    >
                        {{ t("vue.kiosk_ai_feedback_clear_filters") }}
                    </button>
                </div>

                <div
                    class="overflow-hidden rounded-xl border border-gray-200 bg-white"
                >
                    <p
                        v-if="feedback.rows.length === 0"
                        class="px-5 py-4 text-sm text-gray-400"
                    >
                        {{ t("vue.kiosk_ai_feedback_empty") }}
                    </p>

                    <div v-else class="overflow-x-auto">
                        <table class="w-full text-sm">
                            <thead>
                                <tr class="border-b border-gray-100 text-left">
                                    <th
                                        v-for="column in columns"
                                        :key="column.key ?? column.label"
                                        class="px-5 py-2 text-xs font-medium uppercase tracking-wide text-gray-400"
                                    >
                                        <button
                                            v-if="column.key !== null"
                                            type="button"
                                            class="inline-flex cursor-pointer items-center gap-1 uppercase transition hover:text-gray-600"
                                            :class="
                                                feedback.sort === column.key
                                                    ? 'text-gray-700'
                                                    : ''
                                            "
                                            @click="applySort(column.key)"
                                        >
                                            {{ t(column.label) }}
                                            <span
                                                v-if="
                                                    feedback.sort === column.key
                                                "
                                                aria-hidden="true"
                                                >{{
                                                    feedback.direction === "asc"
                                                        ? "↑"
                                                        : "↓"
                                                }}</span
                                            >
                                        </button>
                                        <span v-else>{{
                                            t(column.label)
                                        }}</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-for="row in feedback.rows"
                                    :key="row.id"
                                    class="border-b border-gray-50 last:border-0"
                                >
                                    <td class="px-5 py-2 whitespace-nowrap">
                                        <span
                                            class="rounded-full px-2 py-0.5 text-xs font-medium"
                                            :class="
                                                row.rating === 'up'
                                                    ? 'bg-emerald-50 text-emerald-600'
                                                    : 'bg-red-50 text-red-500'
                                            "
                                        >
                                            {{
                                                row.rating === "up"
                                                    ? t(
                                                          "vue.kiosk_ai_feedback_rating_up",
                                                      )
                                                    : t(
                                                          "vue.kiosk_ai_feedback_rating_down",
                                                      )
                                            }}
                                        </span>
                                    </td>
                                    <td class="px-5 py-2 text-gray-500">
                                        {{ typeLabel(row.feedback_type) }}
                                    </td>
                                    <td
                                        class="max-w-md px-5 py-2 text-gray-700"
                                    >
                                        <span class="block truncate">
                                            {{ row.reason ?? "—" }}
                                        </span>
                                    </td>
                                    <!-- Text, not a link: a chat belongs to its
                                     author's team, so the admin viewing this
                                     report has no right to open it. -->
                                    <td
                                        class="px-5 py-2 whitespace-nowrap text-gray-500"
                                    >
                                        {{
                                            row.message_id === null
                                                ? t(
                                                      "vue.kiosk_ai_feedback_target_chat",
                                                  )
                                                : t(
                                                      "vue.kiosk_ai_feedback_target_message",
                                                      { id: row.message_id },
                                                  )
                                        }}
                                        <span class="text-gray-300"
                                            >#{{ row.chat_id }}</span
                                        >
                                    </td>
                                    <td class="px-5 py-2">
                                        <inertia-link
                                            :href="
                                                route(
                                                    'kiosk.teams.ai-usage.index',
                                                    { team: row.team_id },
                                                )
                                            "
                                            class="text-gray-700 underline-offset-2 hover:text-gray-900 hover:underline"
                                        >
                                            {{
                                                row.team_name ??
                                                `#${row.team_id}`
                                            }}
                                        </inertia-link>
                                    </td>
                                    <td class="px-5 py-2">
                                        <inertia-link
                                            :href="
                                                route(
                                                    'kiosk.users.ai-usage.index',
                                                    { user: row.user_id },
                                                )
                                            "
                                            class="text-gray-700 underline-offset-2 hover:text-gray-900 hover:underline"
                                        >
                                            {{
                                                row.user_email ??
                                                `#${row.user_id}`
                                            }}
                                        </inertia-link>
                                    </td>
                                    <td
                                        class="px-5 py-2 whitespace-nowrap text-gray-500"
                                    >
                                        {{ row.created_at }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div
                        class="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-5 py-3"
                    >
                        <div class="flex items-center gap-2">
                            <label class="text-xs text-gray-400">
                                {{ t("vue.kiosk_ai_feedback_per_page") }}
                            </label>
                            <select
                                v-model.number="perPage"
                                class="rounded-lg border border-gray-200 px-2 py-1 text-sm text-gray-700 focus:border-gray-400 focus:outline-hidden"
                            >
                                <option
                                    v-for="option in perPageOptions"
                                    :key="option"
                                    :value="option"
                                >
                                    {{ option }}
                                </option>
                            </select>
                        </div>

                        <div class="flex items-center gap-3">
                            <p class="text-xs text-gray-400">
                                {{
                                    t("vue.kiosk_ai_feedback_page", {
                                        page: feedback.page,
                                        last: feedback.last_page,
                                    })
                                }}
                            </p>
                            <button
                                type="button"
                                class="rounded-lg border border-gray-200 px-3 py-1 text-sm text-gray-600 transition disabled:cursor-not-allowed disabled:opacity-40"
                                :class="
                                    feedback.page > 1
                                        ? 'cursor-pointer hover:bg-gray-50'
                                        : ''
                                "
                                :disabled="feedback.page <= 1"
                                @click="goToPage(feedback.page - 1)"
                            >
                                {{ t("vue.kiosk_ai_feedback_previous") }}
                            </button>
                            <button
                                type="button"
                                class="rounded-lg border border-gray-200 px-3 py-1 text-sm text-gray-600 transition disabled:cursor-not-allowed disabled:opacity-40"
                                :class="
                                    feedback.page < feedback.last_page
                                        ? 'cursor-pointer hover:bg-gray-50'
                                        : ''
                                "
                                :disabled="feedback.page >= feedback.last_page"
                                @click="goToPage(feedback.page + 1)"
                            >
                                {{ t("vue.kiosk_ai_feedback_next") }}
                            </button>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import { HandThumbUpIcon, HandThumbDownIcon } from "@heroicons/vue/24/outline";
import { useTranslations } from "@/composables/useTranslations";

type FeedbackSortKey = "created" | "team" | "user" | "type" | "rating";

type FeedbackRating = "up" | "down";

// Shares are complements of each other, so the two always close a full bar.
interface IFeedbackPolarity {
    up_count: number;
    down_count: number;
    total_count: number;
    up_share: number;
    down_share: number;
}

interface IFeedbackSummaryFilter {
    type: string | null;
    team_id: number | null;
    chat_id: number | null;
}

interface IFeedbackSummary {
    has_data: boolean;
    filter: IFeedbackSummaryFilter;
    overall: IFeedbackPolarity;
    chat_level: IFeedbackPolarity;
    message_level: IFeedbackPolarity;
}

interface IPolarityRow {
    key: string;
    label: string;
    polarity: IFeedbackPolarity;
}

interface IFeedbackRow {
    id: number;
    feedback_type: string;
    rating: FeedbackRating;
    reason: string | null;
    chat_id: number;
    message_id: number | null;
    team_id: number;
    team_name: string | null;
    user_id: number;
    user_email: string | null;
    created_at: string;
}

interface IFeedbackColumn {
    // Null for a column that carries no ordering of its own (the reason text).
    key: FeedbackSortKey | null;
    label: string;
}

const props = defineProps<{
    feedback: {
        rows: IFeedbackRow[];
        total_count: number;
        summary: IFeedbackSummary;
        page: number;
        per_page: number;
        last_page: number;
        search: string | null;
        type: string | null;
        available_types: string[];
        team_id: number | null;
        chat_id: number | null;
        rating: FeedbackRating | null;
        target: "chat" | "message" | null;
        sort: FeedbackSortKey;
        direction: "asc" | "desc";
    };
    perPageOptions: number[];
}>();

const { t } = useTranslations();

const summary = computed<IFeedbackSummary>(() => props.feedback.summary);

// The overall bar first, then the same split by what was rated — the answer to
// whether the assistant is judged differently as a whole than answer by answer.
const polarityRows = computed<IPolarityRow[]>(() => [
    {
        key: "overall",
        label: "vue.kiosk_ai_feedback_summary_overall",
        polarity: summary.value.overall,
    },
    {
        key: "chat",
        label: "vue.kiosk_ai_feedback_summary_chat_level",
        polarity: summary.value.chat_level,
    },
    {
        key: "message",
        label: "vue.kiosk_ai_feedback_summary_message_level",
        polarity: summary.value.message_level,
    },
]);

const formatCount = (value: number): string =>
    new Intl.NumberFormat().format(value);

const formatShare = (value: number): string =>
    `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(value)} %`;

const segmentTitle = (
    rating: FeedbackRating,
    polarity: IFeedbackPolarity,
): string => {
    const label =
        rating === "up"
            ? t("vue.kiosk_ai_feedback_rating_up")
            : t("vue.kiosk_ai_feedback_rating_down");
    const count = rating === "up" ? polarity.up_count : polarity.down_count;
    const share = rating === "up" ? polarity.up_share : polarity.down_share;

    return `${label}: ${formatCount(count)} (${formatShare(share)})`;
};

const columns: IFeedbackColumn[] = [
    { key: "rating", label: "vue.kiosk_ai_feedback_rating" },
    { key: "type", label: "vue.kiosk_ai_feedback_type" },
    { key: null, label: "vue.kiosk_ai_feedback_reason" },
    { key: null, label: "vue.kiosk_ai_feedback_target" },
    { key: "team", label: "vue.kiosk_ai_feedback_team" },
    { key: "user", label: "vue.kiosk_ai_feedback_user" },
    { key: "created", label: "vue.kiosk_ai_feedback_created" },
];

// The filter lists the kinds present in the table, so a kind added later shows
// up without a deploy. Only the ones we have a name for are translated; anything
// else is shown as stored rather than hidden.
const TYPE_LABELS: Record<string, string> = {
    quick_thumbs: "vue.kiosk_ai_feedback_type_quick_thumbs",
};

const typeLabel = (value: string): string => {
    const key = TYPE_LABELS[value];

    return key === undefined ? value : t(key);
};

const search = ref<string>(props.feedback.search ?? "");
const type = ref<string>(props.feedback.type ?? "");
const rating = ref<string>(props.feedback.rating ?? "");
const target = ref<string>(props.feedback.target ?? "");
/**
 * Kept as strings: a number input cleared by the user yields an empty string,
 * which has to reach the server as "no filter" rather than as id zero.
 */
const teamId = ref<string>(
    props.feedback.team_id === null ? "" : String(props.feedback.team_id),
);
const chatId = ref<string>(
    props.feedback.chat_id === null ? "" : String(props.feedback.chat_id),
);
const perPage = ref<number>(props.feedback.per_page);

// The dashboard scopes itself, so its filter is separate state that travels in
// its own query keys. Sharing the table's would make the figures restate
// whatever row filter happened to be active.
const summaryType = ref<string>(props.feedback.summary.filter.type ?? "");
const summaryTeamId = ref<string>(
    props.feedback.summary.filter.team_id === null
        ? ""
        : String(props.feedback.summary.filter.team_id),
);
const summaryChatId = ref<string>(
    props.feedback.summary.filter.chat_id === null
        ? ""
        : String(props.feedback.summary.filter.chat_id),
);

const hasFilters = computed<boolean>(
    (): boolean =>
        search.value !== "" ||
        type.value !== "" ||
        rating.value !== "" ||
        target.value !== "" ||
        teamId.value !== "" ||
        chatId.value !== "",
);

const hasSummaryFilters = computed<boolean>(
    (): boolean =>
        summaryType.value !== "" ||
        summaryTeamId.value !== "" ||
        summaryChatId.value !== "",
);

// Anything that changes what is listed starts over at page one; only the paging
// buttons carry a page of their own.
const reload = (params: {
    sort?: FeedbackSortKey;
    direction?: "asc" | "desc";
    perPage?: number;
    page?: number;
}): void => {
    router.get(
        route("kiosk.ai-feedback.index"),
        {
            search: search.value,
            type: type.value,
            rating: rating.value,
            target: target.value,
            team_id: teamId.value,
            chat_id: chatId.value,
            summary_type: summaryType.value,
            summary_team_id: summaryTeamId.value,
            summary_chat_id: summaryChatId.value,
            sort: params.sort ?? props.feedback.sort,
            direction: params.direction ?? props.feedback.direction,
            per_page: params.perPage ?? perPage.value,
            page: params.page ?? 1,
        },
        { preserveState: true, preserveScroll: true, replace: true },
    );
};

let filterTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * One timer for every filter, so a typed term and a cleared set of filters each
 * end up as a single request instead of one per field.
 */
const scheduleReload = (): void => {
    if (filterTimer !== null) {
        clearTimeout(filterTimer);
    }

    filterTimer = setTimeout(() => reload({}), 350);
};

const clearFilters = (): void => {
    search.value = "";
    type.value = "";
    rating.value = "";
    target.value = "";
    teamId.value = "";
    chatId.value = "";
};

const clearSummaryFilters = (): void => {
    summaryType.value = "";
    summaryTeamId.value = "";
    summaryChatId.value = "";
};

// Clicking the active column flips the direction; a new column starts descending.
const applySort = (key: FeedbackSortKey): void => {
    reload({
        sort: key,
        direction:
            props.feedback.sort === key && props.feedback.direction === "desc"
                ? "asc"
                : "desc",
    });
};

const goToPage = (page: number): void => {
    reload({ page });
};

watch(
    [
        search,
        type,
        rating,
        target,
        teamId,
        chatId,
        summaryType,
        summaryTeamId,
        summaryChatId,
    ],
    (): void => {
        scheduleReload();
    },
);

watch(perPage, (value: number): void => {
    reload({ perPage: value });
});
</script>
